import React, { useEffect, useState } from 'react';
import { Table, Input, Button, Space, Select, DatePicker } from 'antd';
import { supabase } from '../../supabaseClient';
import dayjs from 'dayjs';

const UserDeviceList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [dvcId, setDvcId] = useState('');
  const [ispMap, setIspMap] = useState<Record<string, string>>({});
  const [prMap, setPrMap] = useState<Record<string, string>>({});
  const [cityMap, setCityMap] = useState<Record<string, string>>({});
  const [userEmailMap, setUserEmailMap] = useState<Record<string, string>>({});
  const [userEmail, setUserEmail] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  const [ispOptions, setIspOptions] = useState<{ value: string, label: string }[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<{ value: string, label: string }[]>([]);
  const [cityOptions, setCityOptions] = useState<{ value: string, label: string, pr_code: string }[]>([]);
  const [selectedIsp, setSelectedIsp] = useState<string | undefined>();
  const [selectedProvince, setSelectedProvince] = useState<string | undefined>();
  const [selectedCity, setSelectedCity] = useState<string | undefined>();

  const fetchDevices = async () => {
    setLoading(true);
    let query = supabase.from('u_user_device').select('*').order('created_at', { ascending: false });
    let { data: devices, error } = await query;
    if (!error && devices) {
      if (userId) devices = devices.filter((d: any) => d.user_id && d.user_id.includes(userId));
      if (userEmail) devices = devices.filter((d: any) => userEmailMap[d.user_id] && userEmailMap[d.user_id].includes(userEmail));
      if (dvcId) devices = devices.filter((d: any) => d.dvcId && d.dvcId.includes(dvcId));
      if (deviceName) devices = devices.filter((d: any) => d.name && d.name.includes(deviceName));
      if (status !== undefined && status !== '') devices = devices.filter((d: any) => String(d.status) === status);
      if (selectedIsp) devices = devices.filter((d: any) => d.ispId === selectedIsp);
      if (selectedProvince) devices = devices.filter((d: any) => d.cnAreaPrId === selectedProvince);
      if (selectedCity) devices = devices.filter((d: any) => d.cnAreaCityId === selectedCity);
      if (dateRange && dateRange[0]) devices = devices.filter((d: any) => d.created_at && d.created_at >= dateRange[0] + ' 00:00:00');
      if (dateRange && dateRange[1]) devices = devices.filter((d: any) => d.created_at && d.created_at <= dateRange[1] + ' 23:59:59');
      setData(devices);
    } else {
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDevices();
    // 获取运营商映射和下拉选项
    (async () => {
      const { data: isps } = await supabase.from('MGT_DVC_ISP').select('ID,NAME');
      const map: Record<string, string> = {};
      const options: { value: string, label: string }[] = [];
      (isps || []).forEach((item: any) => {
        map[item.ID] = item.NAME;
        options.push({ value: item.ID, label: item.NAME });
      });
      setIspMap(map);
      setIspOptions(options);
    })();
    // 获取省份映射和下拉选项
    (async () => {
      const { data: pros } = await supabase.from('SYS_CN_AREA_PR').select('CODE,NAME');
      const map: Record<string, string> = {};
      const options: { value: string, label: string }[] = [];
      (pros || []).forEach((item: any) => {
        map[item.CODE] = item.NAME;
        options.push({ value: item.CODE, label: item.NAME });
      });
      setPrMap(map);
      setProvinceOptions(options);
    })();
    // 获取城市映射和下拉选项
    (async () => {
      const { data: cities } = await supabase.from('SYS_CN_AREA_CITY').select('CODE,NAME,PR_CODE');
      const map: Record<string, string> = {};
      const options: { value: string, label: string, pr_code: string }[] = [];
      (cities || []).forEach((item: any) => {
        map[item.CODE] = item.NAME;
        options.push({ value: item.CODE, label: item.NAME, pr_code: item.PR_CODE });
      });
      setCityMap(map);
      setCityOptions(options);
    })();
    // 获取用户邮箱映射
    (async () => {
      const { data: users } = await supabase.from('u_profiles').select('user_id,email');
      const map: Record<string, string> = {};
      (users || []).forEach((item: any) => {
        map[item.user_id] = item.email;
      });
      setUserEmailMap(map);
    })();
  }, []);

  // 设备绑定状态映射
  const bindStatusMap: Record<string | number, string> = {
    0: '等待绑定',
    1: '绑定成功',
    2: '绑定失败',
  };

  const statusOptions = [
    { value: '', label: '全部状态' },
    { value: '0', label: '等待绑定' },
    { value: '1', label: '绑定成功' },
    { value: '2', label: '绑定失败' },
  ];

  const columns = [
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160, ellipsis: true, render: (text: string) => text ? new Date(text).toLocaleString('zh-CN', { hour12: false }) : '' },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 300, ellipsis: false },
    { title: '用户邮箱', dataIndex: 'user_id', key: 'email', width: 200, ellipsis: true, render: (user_id: string) => userEmailMap[user_id] || '' },
    { title: '设备编号', dataIndex: 'dvcId', key: 'dvcId', width: 160, ellipsis: true },
    { title: '设备名称', dataIndex: 'name', key: 'name', width: 120, ellipsis: true },
    { title: '运营商', dataIndex: 'ispId', key: 'ispId', width: 120, ellipsis: true, render: (id: string) => ispMap[id] || id },
    { title: '省', dataIndex: 'cnAreaPrId', key: 'province', width: 100, ellipsis: true, render: (code: string) => prMap[code] || code },
    { title: '市', dataIndex: 'cnAreaCityId', key: 'city', width: 100, ellipsis: true, render: (code: string) => cityMap[code] || code },
    { title: '备注', dataIndex: 'mgtRmk', key: 'mgtRmk', width: 120, ellipsis: true },
    { title: '状态', dataIndex: 'status', key: 'status', width: 80, ellipsis: true, render: (v: string|number) => bindStatusMap[v] ?? v },
  ];

  // 城市下拉联动过滤
  const filteredCityOptions = selectedProvince ? cityOptions.filter(c => c.pr_code === selectedProvince) : cityOptions;

  return (
    <div style={{ overflowX: 'auto', background: '#fff', padding: 12 }}>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap', display: 'flex' }} size={[8, 8]}>
        <Input
          style={{ width: 180 }}
          placeholder="用户ID"
          value={userId}
          onChange={e => setUserId(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 180 }}
          placeholder="用户邮箱"
          value={userEmail}
          onChange={e => setUserEmail(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 180 }}
          placeholder="设备编号"
          value={dvcId}
          onChange={e => setDvcId(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 180 }}
          placeholder="设备名称"
          value={deviceName}
          onChange={e => setDeviceName(e.target.value)}
          allowClear
        />
        <Select
          allowClear
          style={{ width: 140 }}
          placeholder="运营商"
          value={selectedIsp}
          onChange={v => setSelectedIsp(v)}
          options={ispOptions}
        />
        <Select
          allowClear
          style={{ width: 140 }}
          placeholder="省"
          value={selectedProvince}
          onChange={v => { setSelectedProvince(v); setSelectedCity(undefined); }}
          options={provinceOptions}
        />
        <Select
          allowClear
          style={{ width: 140 }}
          placeholder="市"
          value={selectedCity}
          onChange={v => setSelectedCity(v)}
          options={filteredCityOptions}
        />
        <Select
            allowClear
            style={{ width: 140 }}
            placeholder="状态"
            value={status}
            onChange={v => setStatus(v)}
            options={statusOptions}
        />
        <DatePicker.RangePicker
          style={{ width: 360 }}
          placeholder={['开始时间', '结束时间']}
          value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
          onChange={dates => setDateRange(
            dates && dates[0] && dates[1]
              ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
              : null
          )}
          allowClear
        />
        <Button type="primary" onClick={fetchDevices} loading={loading}>筛选</Button>
        <Button
          onClick={() => {
            setUserId('');
            setUserEmail('');
            setDvcId('');
            setDeviceName('');
            setStatus(undefined);
            setSelectedIsp(undefined);
            setSelectedProvince(undefined);
            setSelectedCity(undefined);
            setDateRange(null);
            fetchDevices();
          }}
        >重置</Button>
      </Space>
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={data}
        loading={loading}
        bordered
        pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (total) => `共 ${total} 条数据`, position: ['bottomRight'] }}
        scroll={{ x: 1600 }}
        size="small"
        style={{ minWidth: 1200 }}
      />
    </div>
  );
};

export default UserDeviceList;
